import { ArrowRight, BarChart3 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type { ReportTab } from '@/features/Strategies/features/Report/types';
import { formatKpmValue } from '@/features/Strategies/features/Report/utils/kpmFormat';
import type { BacktestReport } from '../hooks/useSoteriaChat';

// Human label for the section Soteria just discussed — shown as the card
// subline and reflected in the tab we deep-link to.
const SECTION_LABELS: Record<string, string> = {
  performance: 'Performance',
  attribution: 'Risk & Attribution',
  portfolio: 'Constituents',
  turnover: 'Turnover',
};

/**
 * BacktestReportCard
 * ------------------
 * Rendered under a Soteria reply when the user asked how a completed backtest
 * did. Instead of the agent pasting a raw `/strategies/…/report` path, this
 * card carries the link — deep-linked (via `?tab=`) to the exact report tab
 * matching the section Soteria discussed. Mirrors the completed variant of
 * BacktestStartedCard so the two read as one family.
 */
export function BacktestReportCard({ report }: { report: BacktestReport }) {
  const tab = (report.report_tab ?? 'performance') as ReportTab;
  const sectionLabel =
    SECTION_LABELS[report.section ?? 'performance'] ?? 'Performance';

  return (
    <SoteriaCard
      variant="success"
      icon={<BarChart3 className="h-4 w-4" />}
      eyebrow="Backtest report"
      title={report.strategy_name}
      subtitle={`${sectionLabel} · full breakdown, charts, and metrics`}
      cta={
        <Link
          to={report.report_url}
          search={{ tab }}
          className={soteriaCardCtaClass('success')}
        >
          View report
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
    >
      {/* Headline metric tiles — verbatim from the report's Key Performance
          Metrics card, written with the report card's own per-label rule
          (`formatKpmValue`), so 0.057 under "CAGR%" reads 5.70% here as it
          does on the report. Only the performance section carries them. */}
      {report.stats && report.stats.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-success/20 pt-3 sm:grid-cols-3">
          {report.stats.map((tile, i) => (
            <div
              key={i}
              className="rounded-lg bg-card px-3 py-2 ring-1 ring-success/20"
            >
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {tile.label}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">
                {tile.value == null ? '—' : formatKpmValue(tile.value, tile.label)}
              </p>
              {tile.benchmark != null && tile.benchmark !== '' && (
                <p className="text-[10px] text-muted-foreground">
                  vs {formatKpmValue(tile.benchmark, tile.label)} bmk
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </SoteriaCard>
  );
}
